/*
 * Copyright (c) 2026 Joseph Duggan. All rights reserved.
 */

import { appPalette } from '@sidekick/design-tokens';
import { useState, type SyntheticEvent } from 'react';
import { monoLabel, inputStyle } from '../../lib/ui.js';
import { trpc } from '../../lib/trpc.js';

const p = appPalette;

type CaptureMode = 'thought' | 'task' | 'waiting';

const MODE_LABELS: Record<CaptureMode, string> = {
  thought: 'Thought',
  task: 'Task',
  waiting: 'Waiting on',
};

interface Suggestion {
  captureId: string;
  rawText: string;
  disposition: 'next_action' | 'waiting_for' | 'project' | 'someday' | 'discard';
  actionDescription: string | null;
  context: string | null;
  isTwoMinute: boolean;
  ownerName: string | null;
  projectTitle: string | null;
  desiredOutcome: string | null;
  highImpact: boolean;
  reasoning: string;
}

export function Inbox() {
  const utils = trpc.useUtils();
  const list = trpc.captures.list.useQuery();
  const create = trpc.captures.create.useMutation({
    onSuccess: async () => {
      await utils.captures.list.invalidate();
    },
  });
  const promote = trpc.captures.promote.useMutation({
    onSuccess: async () => {
      // Both queries change shape: capture leaves the inbox, action
      // appears on the desk.
      await Promise.all([utils.captures.list.invalidate(), utils.actions.list.invalidate()]);
    },
  });
  const discard = trpc.captures.discard.useMutation({
    onSuccess: async () => {
      await utils.captures.list.invalidate();
    },
  });
  // Process mode: the LLM proposes a disposition per capture. Applying a
  // suggestion is composed from the existing mutations — the mode itself
  // never mutates, so the user stays in control.
  const suggest = trpc.process.suggest.useMutation();
  const actionsCreate = trpc.actions.create.useMutation({
    onSuccess: async () => {
      await utils.actions.list.invalidate();
    },
  });
  const projectsCreate = trpc.projects.create.useMutation({
    onSuccess: async () => {
      await utils.projects.list.invalidate();
    },
  });

  const [draft, setDraft] = useState('');
  const [mode, setMode] = useState<CaptureMode>('thought');
  const [owner, setOwner] = useState('');
  // Brief confirmation shown after a Task/Waiting-on capture lands somewhere
  // off The Letter; cleared the moment the user types again.
  const [confirmation, setConfirmation] = useState('');
  // capture ids whose suggestion the user has applied or dismissed; they
  // drop out of the suggestions panel without a re-fetch.
  const [resolved, setResolved] = useState<ReadonlySet<string>>(new Set());

  const submitting = create.isPending || actionsCreate.isPending;
  const text = draft.trim();
  const ownerName = owner.trim();
  const canSubmit =
    text !== '' && (mode !== 'waiting' || ownerName !== '') && !submitting;

  const onSubmit = (e: SyntheticEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    if (mode === 'thought') {
      create.mutate(
        { rawText: text },
        { onSuccess: () => setDraft('') },
      );
      return;
    }
    if (mode === 'task') {
      actionsCreate.mutate(
        { description: text, estimatedMinutes: null, status: 'next' },
        {
          onSuccess: () => {
            setDraft('');
            setConfirmation('Added to The Docket ↗');
          },
        },
      );
      return;
    }
    actionsCreate.mutate(
      { description: text, estimatedMinutes: null, status: 'waiting', ownerName },
      {
        onSuccess: () => {
          setDraft('');
          setOwner('');
          setConfirmation('Added to Waiting on ↗');
        },
      },
    );
  };

  const onDraftChange = (next: string) => {
    setDraft(next);
    if (confirmation) setConfirmation('');
  };

  const selectMode = (next: CaptureMode) => {
    setMode(next);
    setConfirmation('');
  };

  const captures = list.data ?? [];
  const applying = actionsCreate.isPending || projectsCreate.isPending || discard.isPending;
  const suggestions = (suggest.data?.suggestions ?? []).filter((s) => !resolved.has(s.captureId));

  const onProcess = () => {
    setResolved(new Set());
    suggest.mutate(undefined);
  };

  const markResolved = (captureId: string) => setResolved((prev) => new Set(prev).add(captureId));

  const applySuggestion = async (s: Suggestion) => {
    const owner = s.ownerName?.trim() ?? '';
    if (s.disposition === 'next_action' || (s.disposition === 'waiting_for' && owner === '')) {
      await actionsCreate.mutateAsync({
        description: s.actionDescription ?? s.rawText,
        estimatedMinutes: null,
        status: 'next',
      });
    } else if (s.disposition === 'waiting_for') {
      await actionsCreate.mutateAsync({
        description: s.actionDescription ?? s.rawText,
        estimatedMinutes: null,
        status: 'waiting',
        ownerName: owner,
      });
    } else if (s.disposition === 'project') {
      await projectsCreate.mutateAsync({
        title: s.projectTitle ?? s.rawText,
        desiredOutcome: s.desiredOutcome ?? null,
      });
      if (s.actionDescription) {
        await actionsCreate.mutateAsync({
          description: s.actionDescription,
          estimatedMinutes: null,
          status: 'next',
        });
      }
    }
    // Every applied disposition removes the source capture from the inbox
    // (someday/discard just clear it — there's no Someday store yet).
    await discard.mutateAsync({ captureId: s.captureId });
    markResolved(s.captureId);
  };

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 10,
        }}
      >
        <span style={{ ...monoLabel }}>Inbox · brain dump</span>
        {captures.length > 0 ? (
          <button
            type="button"
            onClick={onProcess}
            disabled={suggest.isPending}
            title="Triage the inbox with the Process coach"
            style={{
              background: 'transparent',
              border: `1px solid ${p.line}`,
              color: p.muted,
              fontSize: 12,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              padding: '4px 10px',
              cursor: suggest.isPending ? 'wait' : 'pointer',
            }}
          >
            {suggest.isPending ? 'Processing…' : 'Process'}
          </button>
        ) : null}
      </div>

      {suggest.error ? (
        <span style={{ ...monoLabel, color: p.accent }}>{suggest.error.message}</span>
      ) : null}
      {suggest.isSuccess && suggestions.length === 0 ? (
        <span style={{ ...monoLabel, color: p.muted }}>Inbox triaged.</span>
      ) : null}
      {suggestions.length > 0 ? (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {suggestions.map((s) => (
            <SuggestionRow
              key={s.captureId}
              suggestion={s}
              pending={applying}
              onApply={() => applySuggestion(s)}
              onDismiss={() => markResolved(s.captureId)}
            />
          ))}
        </ul>
      ) : null}

      {list.isPending ? (
        <span style={{ ...monoLabel, marginTop: 8 }}>…</span>
      ) : captures.length === 0 ? (
        <p
          style={{
            fontFamily: 'var(--font-serif)',
            fontFeatureSettings: 'var(--serif-feat)',
            fontStyle: 'italic',
            fontSize: 15,
            color: p.muted,
            margin: 0,
          }}
        >
          Nothing dropped today.
        </p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {captures.map((c) => (
            <CaptureRow
              key={c.id}
              rawText={c.rawText}
              capturedAt={new Date(c.capturedAt)}
              onPromote={() => promote.mutate({ captureId: c.id })}
              onDiscard={() => discard.mutate({ captureId: c.id })}
              pending={promote.isPending || discard.isPending}
            />
          ))}
        </ul>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
        <div role="group" aria-label="Capture type" style={{ display: 'flex', gap: 0 }}>
          {(['thought', 'task', 'waiting'] as const).map((m) => {
            const active = mode === m;
            return (
              <button
                key={m}
                type="button"
                onClick={() => selectMode(m)}
                aria-pressed={active}
                style={{
                  background: active ? p.fg : 'transparent',
                  color: active ? p.bg : p.muted,
                  border: `1px solid ${p.line}`,
                  borderRight: m === 'waiting' ? `1px solid ${p.line}` : 'none',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  padding: '5px 12px',
                  cursor: 'pointer',
                }}
              >
                {MODE_LABELS[m]}
              </button>
            );
          })}
        </div>

        <form onSubmit={onSubmit} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          {mode === 'waiting' ? (
            <input
              type="text"
              value={owner}
              onChange={(e) => setOwner(e.target.value)}
              placeholder="Who"
              aria-label="Waiting on owner"
              style={{ ...inputStyle, width: 140 }}
            />
          ) : null}
          <textarea
            value={draft}
            onChange={(e) => onDraftChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                onSubmit(e);
              }
            }}
            placeholder={
              mode === 'thought'
                ? 'Drop a thought'
                : mode === 'task'
                  ? 'Add a next action'
                  : "What you're waiting on"
            }
            aria-label="Capture"
            rows={2}
            style={{ ...inputStyle, flex: 1, resize: 'vertical', minHeight: 56 }}
          />
          <button
            type="submit"
            disabled={!canSubmit}
            style={{
              padding: '10px 18px',
              background: p.fg,
              color: p.bg,
              border: 'none',
              fontSize: 13,
              cursor: !canSubmit ? 'not-allowed' : 'pointer',
              opacity: !canSubmit ? 0.6 : 1,
            }}
          >
            {submitting ? 'Adding…' : 'Add →'}
          </button>
        </form>
      </div>
      {confirmation ? (
        <span style={{ ...monoLabel, color: p.muted }}>{confirmation}</span>
      ) : null}
      {create.error ? (
        <span style={{ ...monoLabel, color: p.accent }}>{create.error.message}</span>
      ) : null}
      {actionsCreate.error ? (
        <span style={{ ...monoLabel, color: p.accent }}>{actionsCreate.error.message}</span>
      ) : null}
    </section>
  );
}

const DISPOSITION_LABEL: Record<Suggestion['disposition'], string> = {
  next_action: 'Next action',
  waiting_for: 'Waiting on',
  project: 'Project',
  someday: 'Someday',
  discard: 'Discard',
};

function SuggestionRow({
  suggestion,
  pending,
  onApply,
  onDismiss,
}: {
  suggestion: Suggestion;
  pending: boolean;
  onApply: () => void;
  onDismiss: () => void;
}) {
  const s = suggestion;
  const detail =
    s.disposition === 'project'
      ? `${s.projectTitle ?? s.rawText}${s.desiredOutcome ? ` — ${s.desiredOutcome}` : ''}`
      : s.disposition === 'waiting_for'
        ? `${s.ownerName ? `${s.ownerName}: ` : ''}${s.actionDescription ?? s.rawText}`
        : s.disposition === 'next_action'
          ? `${s.actionDescription ?? s.rawText}${s.context ? ` · ${s.context}` : ''}`
          : null;

  return (
    <li
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        padding: '10px 0',
        borderTop: `1px solid ${p.line}`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ ...monoLabel, color: p.fg }}>{DISPOSITION_LABEL[s.disposition]}</span>
        {s.highImpact ? <span style={{ ...monoLabel, color: p.accent }}>★ high impact</span> : null}
        {s.isTwoMinute ? <span style={{ ...monoLabel, color: p.muted }}>2-min</span> : null}
      </div>
      {detail ? (
        <span
          style={{
            fontFamily: 'var(--font-serif)',
            fontFeatureSettings: 'var(--serif-feat)',
            fontSize: 15,
            color: p.fg,
          }}
        >
          {detail}
        </span>
      ) : null}
      <span style={{ ...monoLabel, color: p.muted }}>“{s.rawText}”</span>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginTop: 2 }}>
        <span
          style={{
            fontFamily: 'var(--font-serif)',
            fontFeatureSettings: 'var(--serif-feat)',
            fontStyle: 'italic',
            fontSize: 13,
            color: p.muted,
            flex: 1,
          }}
        >
          {s.reasoning}
        </span>
        <button
          type="button"
          onClick={onApply}
          disabled={pending}
          style={{
            background: 'transparent',
            border: 'none',
            color: p.fg,
            cursor: pending ? 'wait' : 'pointer',
            fontSize: 12,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            padding: '0 4px',
          }}
        >
          Apply
        </button>
        <button
          type="button"
          onClick={onDismiss}
          disabled={pending}
          style={{
            background: 'transparent',
            border: 'none',
            color: p.muted,
            cursor: pending ? 'wait' : 'pointer',
            fontSize: 12,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            padding: '0 4px',
          }}
        >
          Dismiss
        </button>
      </div>
    </li>
  );
}

function CaptureRow({
  rawText,
  capturedAt,
  onPromote,
  onDiscard,
  pending,
}: {
  rawText: string;
  capturedAt: Date;
  onPromote: () => void;
  onDiscard: () => void;
  pending: boolean;
}) {
  return (
    <li
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto auto auto',
        gap: 12,
        padding: '10px 0',
        borderTop: `1px solid ${p.line}`,
        alignItems: 'baseline',
      }}
    >
      <span
        style={{
          fontFamily: 'var(--font-serif)',
          fontFeatureSettings: 'var(--serif-feat)',
          fontSize: 15,
          color: p.fg,
          whiteSpace: 'pre-wrap',
        }}
      >
        {rawText}
      </span>
      <span style={{ ...monoLabel, color: p.muted }}>{relativeTime(capturedAt)}</span>
      <button
        type="button"
        onClick={onPromote}
        disabled={pending}
        title="Promote to a next action"
        aria-label="Promote to a next action"
        style={{
          background: 'transparent',
          border: 'none',
          color: p.muted,
          cursor: pending ? 'wait' : 'pointer',
          fontSize: 14,
          padding: '0 4px',
          letterSpacing: '0.1em',
        }}
      >
        →
      </button>
      <button
        type="button"
        onClick={onDiscard}
        disabled={pending}
        title="Discard (not worth doing)"
        aria-label="Discard"
        style={{
          background: 'transparent',
          border: 'none',
          color: p.muted,
          cursor: pending ? 'wait' : 'pointer',
          fontSize: 16,
          padding: '0 4px',
        }}
      >
        ×
      </button>
    </li>
  );
}

function relativeTime(d: Date): string {
  const diffMs = Date.now() - d.getTime();
  const diffSec = Math.max(0, Math.floor(diffMs / 1000));
  if (diffSec < 60) return 'just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay === 1) return 'yesterday';
  return `${diffDay}d ago`;
}
